// src/components/tasks/ListsPanel.tsx
// Bottom sheet panel for quick access to lists
import React, { useMemo } from 'react';
import { IonModal, IonIcon, IonSpinner } from '@ionic/react';
import { add, chevronForward, pinOutline } from 'ionicons/icons';
import { IconDisplay } from '../ui/IconPicker';
import type { TaskList, ListType } from '../../types';
import './tasks.css';

export interface ListsPanelProps {
  isOpen: boolean;
  onClose: () => void;
  lists: TaskList[];
  loading?: boolean;
  onListSelect: (list: TaskList) => void;
  onCreateList: (type?: ListType) => void;
}

// List type configuration
const LIST_TYPE_CONFIG: Record<ListType, { label: string; icon: string; pluralLabel: string }> = {
  todo: { label: 'To-Do', icon: '✅', pluralLabel: 'To-Do Lists' },
  shopping: { label: 'Shopping', icon: '🛒', pluralLabel: 'Shopping Lists' },
  project: { label: 'Project', icon: '📋', pluralLabel: 'Projects' },
  checklist: { label: 'Checklist', icon: '📝', pluralLabel: 'Checklists' },
};

export const ListsPanel: React.FC<ListsPanelProps> = ({
  isOpen,
  onClose,
  lists,
  loading = false,
  onListSelect,
  onCreateList,
}) => {
  // Separate pinned lists
  const pinnedLists = useMemo(
    () => lists.filter(list => list.is_pinned && !list.is_archived),
    [lists]
  );

  // Group remaining lists by type
  const listsByType = useMemo(() => {
    const activeLists = lists.filter(list => !list.is_pinned && !list.is_archived);
    return activeLists.reduce((acc, list) => {
      const type = list.list_type || 'todo';
      if (!acc[type]) acc[type] = [];
      acc[type].push(list);
      return acc;
    }, {} as Record<ListType, TaskList[]>);
  }, [lists]);

  // Calculate total items per type
  const typeStats = useMemo(() => {
    const stats: Record<ListType, { count: number; itemCount: number }> = {
      todo: { count: 0, itemCount: 0 },
      shopping: { count: 0, itemCount: 0 },
      project: { count: 0, itemCount: 0 },
      checklist: { count: 0, itemCount: 0 },
    };

    lists.filter(l => !l.is_archived).forEach(list => {
      const type = list.list_type || 'todo';
      stats[type].count++;
      stats[type].itemCount += list.item_count || 0;
    });

    return stats;
  }, [lists]);

  return (
    <IonModal
      isOpen={isOpen}
      onDidDismiss={onClose}
      className="lists-panel"
      initialBreakpoint={0.5}
      breakpoints={[0, 0.25, 0.5, 0.75, 1]}
      handleBehavior="cycle"
    >
      <div className="lists-panel__content">
        {/* Header */}
        <div className="lists-panel__header">
          <h2 className="lists-panel__title">Lists</h2>
          <button
            className="lists-panel__add-btn"
            onClick={() => onCreateList()}
            aria-label="Create new list"
          >
            <IonIcon icon={add} />
          </button>
        </div>

        {loading ? (
          <div className="lists-panel__loading">
            <IonSpinner name="crescent" />
          </div>
        ) : (
          <>
            {/* Pinned Lists */}
            {pinnedLists.length > 0 && (
              <section className="lists-panel__section">
                <h3 className="lists-panel__section-title">
                  <IonIcon icon={pinOutline} />
                  Pinned
                </h3>
                <div className="lists-panel__pinned">
                  {pinnedLists.map(list => (
                    <button
                      key={list.id}
                      className="list-card"
                      onClick={() => onListSelect(list)}
                    >
                      <span className="list-card__icon">
                        {list.icon_type && list.icon_type !== 'emoji' && list.icon_url ? (
                          <IconDisplay
                            icon={{ type: list.icon_type, name: list.icon || '', imageUrl: list.icon_url }}
                            size={20}
                          />
                        ) : (
                          list.icon || '📋'
                        )}
                      </span>
                      <span className="list-card__name">{list.name}</span>
                      {(list.item_count ?? 0) > 0 && (
                        <span className="list-card__count">{list.item_count}</span>
                      )}
                    </button>
                  ))}
                </div>
              </section>
            )}

            {/* Lists by Type */}
            <section className="lists-panel__section">
              <h3 className="lists-panel__section-title">All Lists</h3>
              <div className="lists-panel__types">
                {(Object.keys(LIST_TYPE_CONFIG) as ListType[]).map(type => {
                  const config = LIST_TYPE_CONFIG[type];
                  const stats = typeStats[type];

                  return (
                    <button
                      key={type}
                      className="list-type-row"
                      onClick={() => {
                        // If there's only one list of this type, open it directly
                        const typeLists = listsByType[type] || [];
                        if (typeLists.length === 1) {
                          onListSelect(typeLists[0]);
                        } else {
                          // Otherwise, could open a filtered view or create new
                          onCreateList(type);
                        }
                      }}
                    >
                      <span className="list-type-row__icon">{config.icon}</span>
                      <span className="list-type-row__label">{config.pluralLabel}</span>
                      <span className="list-type-row__count">
                        {stats.count} {stats.count === 1 ? 'list' : 'lists'}
                        {stats.itemCount > 0 && ` • ${stats.itemCount} items`}
                      </span>
                      <IonIcon icon={chevronForward} />
                    </button>
                  );
                })}
              </div>
            </section>

            {/* Quick create buttons */}
            <section className="lists-panel__section">
              <div className="lists-panel__quick-create">
                {(Object.keys(LIST_TYPE_CONFIG) as ListType[]).map(type => {
                  const config = LIST_TYPE_CONFIG[type];
                  return (
                    <button
                      key={type}
                      className="lists-panel__quick-create-btn"
                      onClick={() => onCreateList(type)}
                    >
                      <span>{config.icon}</span>
                      <span>New {config.label}</span>
                    </button>
                  );
                })}
              </div>
            </section>
          </>
        )}
      </div>
    </IonModal>
  );
};

ListsPanel.displayName = 'ListsPanel';

export default ListsPanel;
